/* eslint-disable no-underscore-dangle */
/* eslint-disable @angular-eslint/no-host-metadata-property */
import { AnimationEvent } from '@angular/animations';
import { Directionality } from '@angular/cdk/bidi';
import { CdkStepper, StepContentPositionState } from '@angular/cdk/stepper';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { NStepHeaderComponent } from './n-step-header.component';
import { NStepComponent } from './n-step.component';
import {
  DEFAULT_HORIZONTAL_ANIMATION_DURATION,
  DEFAULT_VERTICAL_ANIMATION_DURATION,
  nStepperAnimations,
} from './n-stepper-animations';

@Component({
  animations: [nStepperAnimations.horizontalStepTransition, nStepperAnimations.verticalStepTransition],
  selector: 'n-stepper, n-vertical-stepper, n-horizontal-stepper, [nStepper]',
  exportAs: 'nStepper, nVerticalStepper, nHorizontalStepper',
  templateUrl: './n-stepper.component.html',
  host: {
    '[class.n-stepper-horizontal]': 'orientation === "horizontal"',
    '[class.n-stepper-vertical]': 'orientation === "vertical"',
    '[class.n-stepper-label-position-end]': 'orientation === "horizontal" && labelPosition === "end"',
    '[class.n-stepper-label-position-bottom]': 'orientation === "horizontal" && labelPosition === "bottom"',
    '[class.n-stepper-header-position-bottom]': 'headerPosition === "bottom"',
    '[attr.aria-orientation]': 'orientation',
    role: 'tablist',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: CdkStepper, useExisting: NStepperComponent }],
})
export class NStepperComponent extends CdkStepper implements AfterContentInit {
  /** The list of step headers of the steps in the stepper. */
  @ViewChildren(NStepHeaderComponent) override _stepHeader: QueryList<NStepHeaderComponent>;

  /** Full list of steps inside the stepper, including inside nested steppers. */
  @ContentChildren(NStepComponent, { descendants: true }) override _steps: QueryList<NStepComponent>;

  /** Event emitted when the current step is done transitioning in. */
  @Output() readonly animationDone: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Whether the label should display in bottom or end position.
   * Only applies in the `horizontal` orientation.
   */
  @Input()
  labelPosition: 'bottom' | 'end' = 'end';

  /**
   * Position of the stepper's header.
   * Only applies in the `horizontal` orientation.
   */
  @Input()
  headerPosition: 'top' | 'bottom' = 'top';

  @Input() pinnedSteps: number[] = [];

  /** Steps that belong to the current stepper, excluding ones from nested steppers. */
  override readonly steps: QueryList<NStepComponent> = new QueryList<NStepComponent>();

  /** Consumer-specified template-refs to be used to override the header icons. */
  // _iconOverrides: Record<string, TemplateRef<MatStepperIconContext>> = {};

  /** Stream of animation `done` events when the body expands/collapses. */
  readonly _animationDone = new Subject<AnimationEvent>();

  private _animationDuration = '';

  constructor(
    @Optional() dir: Directionality,
    changeDetectorRef: ChangeDetectorRef,
    elementRef: ElementRef<HTMLElement>,
  ) {
    super(dir, changeDetectorRef, elementRef);
    const nodeName = elementRef.nativeElement.nodeName.toLowerCase();
    this.orientation = nodeName === 'n-vertical-stepper' ? 'vertical' : 'horizontal';
  }

  /** Duration for the animation. Will be normalized to milliseconds if no units are set. */
  @Input()
  get animationDuration(): string {
    return this._animationDuration;
  }
  set animationDuration(value: string) {
    this._animationDuration = /^\d+$/.test(value) ? `${value}ms` : value;
  }

  override ngAfterContentInit() {
    super.ngAfterContentInit();

    // Mark the component for change detection whenever the content children query changes
    this.steps.changes.pipe(takeUntil(this._destroyed)).subscribe(() => {
      this._stateChanged();
    });

    this._animationDone.pipe(takeUntil(this._destroyed)).subscribe((event) => {
      if ((event.toState as StepContentPositionState) === 'current') {
        this.animationDone.emit();
      }
    });
  }

  isStepPinned(index: number): boolean {
    return this.pinnedSteps.includes(index);
  }

  stepIsNavigable(index: number, step: NStepComponent): boolean {
    return step.completed || this.selectedIndex === index || !this.linear;
  }

  getAnimationDuration() {
    if (this.animationDuration) {
      return this.animationDuration;
    }

    return this.orientation === 'horizontal'
      ? DEFAULT_HORIZONTAL_ANIMATION_DURATION
      : DEFAULT_VERTICAL_ANIMATION_DURATION;
  }
}
